import {
    Box,
    Typography,
    Skeleton,
    Stack,
    IconButton,
    Tooltip,
} from "@mui/joy";
import { type ReactNode, useState } from "react";
import { Calculator, Plus } from "lucide-react";
import { MaterialCard } from "./MaterialCard";
import {
    MaterialGroupBlock,
    MaterialSubgroupBlock,
} from "./MaterialGroupBlock";
import {
    materialGlassPanelSx,
    MATERIAL_ROW,
    materialRowDividerSx,
} from "./materialRowLayout";
import type {
    EditableMaterialCardProps,
    EditHandlers,
    FurnaceVentClusterProps,
    GroupRowsProps,
    MaterialRowProps,
    MaterialsProps,
} from "./types";
import {
    useMaterialEvaluator,
    useMaterialProductSelection,
    useMaterialAttributeDefaults,
    useEstimateContractContextManager,
    useEstimateOptionsSession,
} from "../../hooks";
import type {
    FurnaceVentMaterialRow,
    MaterialRowItem,
} from "../../utils/materialLineItems";
import { buildFurnaceVentTreeBySize } from "../../utils/materialLineItems";
import { EstimateSupplierComparison } from "..";
import EstimateOptionsCompareBoard from "../EstimateOptionsCompareBoard";
import {
    findOptionByKey,
    resolveVariantProductId,
} from "../../utils/materialColorSelection";
import { useNotification } from "../../../../hooks";

const FIELD_SHINGLES_CATEGORY = "Field Shingles";

function normalizeRoofColor(value: unknown): string {
    if (value == null || value === 0) return "";
    return String(value);
}

export default function Materials({
    projectId,
    materialsByGroup,
    loading: materialsLoading = false,
    error: materialsError = null,
}: MaterialsProps) {
    const {
        loading: evalLoading,
        error: evalError,
        formulaFlags,
    } = useMaterialEvaluator(materialsByGroup);
    const { values, patchMaterialEdit, patchMaterialEdits, patchAttributes } =
        useEstimateContractContextManager();
    const { onProductChange, onCatalogColorChange } =
        useMaterialProductSelection(materialsByGroup);
    useMaterialAttributeDefaults(materialsByGroup);
    const { showError } = useNotification();
    const {
        options: estimateOptions,
        estimateOptionId,
        loading: optionsLoading,
        compareOpen,
        setCompareOpen,
        syncFromShingles,
        activateOption,
        openOption,
    } = useEstimateOptionsSession(projectId);

    const [pricingRefreshKey, setPricingRefreshKey] = useState(0);

    const loading = materialsLoading || evalLoading;
    const error = materialsError || evalError;

    const fieldShingleMultiValues = (() => {
        if (estimateOptions.length >= 2) {
            return estimateOptions
                .map((o) => o.DriverProductId)
                .filter(Boolean) as string[];
        }
        return [];
    })();

    const handleFieldShingleMultiChange = async (
        editKey: string,
        rowId: string,
        optionKeys: string[],
        products: import("../../types").MaterialLineItemOption[],
    ) => {
        const productIds = optionKeys
            .map((key) => {
                const option = findOptionByKey(products, key);
                return resolveVariantProductId(option, null);
            })
            .filter(Boolean) as string[];

        // Keep at least one shingle selected
        if (productIds.length === 0) return;

        try {
            // Always sync shortlist (including collapse to 1) so options archive correctly
            await syncFromShingles(productIds);
            setPricingRefreshKey((key) => key + 1);
            if (productIds.length === 1) {
                onProductChange(
                    editKey,
                    FIELD_SHINGLES_CATEGORY,
                    rowId,
                    optionKeys[0] || null,
                    products,
                );
            }
        } catch (err: any) {
            showError(err?.message || "Failed to update shingle options");
        }
    };

    const handleApplyAllFormulas = () => {
        const patches: Record<
            string,
            { quantity: number; quantityOverridden: boolean }
        > = {};

        for (const [editKey, flag] of Object.entries(formulaFlags)) {
            if (flag?.result == null) continue;
            patches[editKey] = {
                quantity: Number(flag.result),
                quantityOverridden: false,
            };
        }

        if (Object.keys(patches).length === 0) return;
        patchMaterialEdits(patches);
    };

    const canApplyAllFormulas = Object.values(formulaFlags).some(
        (flag) => flag?.result != null,
    );

    const handlers: EditHandlers = {
        edits: values.materials,
        formulaFlags,
        onProductChange: (editKey, category, rowId, optionKey, options) => {
            onProductChange(editKey, category, rowId, optionKey, options);
        },
        onQuantityChange: (editKey, quantity) => {
            patchMaterialEdit(editKey, { quantity, quantityOverridden: true });
        },
        onResetToFormula: (editKey) => {
            const result = formulaFlags[editKey]?.result;
            if (result == null) return;
            patchMaterialEdit(editKey, {
                quantity: Number(result),
                quantityOverridden: false,
            });
        },
        onCatalogColorChange: (editKey, color, options) => {
            onCatalogColorChange(editKey, color, options);
        },
        getColor: (category) => {
            if (category !== FIELD_SHINGLES_CATEGORY) return "";
            return normalizeRoofColor(values.attributes?.RoofColor);
        },
        onColorChange: (category, color) => {
            if (category !== FIELD_SHINGLES_CATEGORY) return;
            patchAttributes({ RoofColor: color });
        },
        fieldShingleMultiValues:
            fieldShingleMultiValues.length > 0
                ? fieldShingleMultiValues
                : undefined,
        onFieldShingleMultiChange: (editKey, rowId, optionKeys, options) => {
            void handleFieldShingleMultiChange(
                editKey,
                rowId,
                optionKeys,
                options,
            );
        },
    };

    if (loading) {
        return (
            <Box sx={{ mt: 2 }}>
                <Stack spacing={2}>
                    {[1, 2, 3].map((i) => (
                        <Skeleton key={i} variant="rectangular" height={56} />
                    ))}
                </Stack>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ mt: 2, py: 3, textAlign: "center" }}>
                <Typography level="body-sm" color="danger">
                    {error}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ mt: 1 }}>
            {compareOpen && (
                <EstimateOptionsCompareBoard
                    projectId={projectId}
                    options={estimateOptions}
                    openOptionId={estimateOptionId}
                    loading={optionsLoading}
                    refreshKey={pricingRefreshKey}
                    onOpen={openOption}
                    onActivate={(id) => {
                        void activateOption(id);
                    }}
                    onClose={() => setCompareOpen(false)}
                />
            )}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 2,
                }}
            >
                <Box>
                    <Typography level="title-md">
                        Material & Component Selection
                    </Typography>
                    <Typography level="body-sm" color="neutral">
                        Product specifications, manufacturer comparisons, and
                        inventory counts.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                    <Tooltip
                        title="Reset all to formula"
                        placement="top"
                        size="sm"
                    >
                        <span>
                            <IconButton
                                size="sm"
                                variant="soft"
                                color="neutral"
                                disabled={!canApplyAllFormulas}
                                onClick={handleApplyAllFormulas}
                                aria-label="Reset all to formula"
                                sx={{
                                    borderRadius: "4px",
                                    border: "1px solid",
                                    borderColor: "divider",
                                }}
                            >
                                <Calculator size={16} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Add material" placement="top" size="sm">
                        <IconButton
                            size="sm"
                            variant="soft"
                            color="neutral"
                            aria-label="Add material"
                            sx={{
                                borderRadius: "4px",
                                border: "1px solid",
                                borderColor: "divider",
                            }}
                        >
                            <Plus size={16} />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Box>

            <Stack spacing={2.5}>
                {Object.entries(materialsByGroup).map(([groupName, rows]) => (
                    <Box
                        key={groupName}
                        sx={{
                            ...materialGlassPanelSx,
                            mx: { xs: -2, sm: -3 },
                        }}
                    >
                        <Box
                            sx={{
                                px: MATERIAL_ROW.px,
                                py: 1.25,
                                ...materialRowDividerSx,
                            }}
                        >
                            <Typography level="title-md">
                                {groupName}
                            </Typography>
                        </Box>

                        <GroupRows rows={rows} handlers={handlers} />
                    </Box>
                ))}

                {Object.keys(materialsByGroup).length === 0 && (
                    <Box
                        sx={{
                            ...materialGlassPanelSx,
                            py: 4,
                            px: 3,
                            textAlign: "center",
                            mx: { xs: -2, sm: -3 },
                        }}
                    >
                        <Typography level="body-sm" color="neutral">
                            No materials found for these specifications.
                        </Typography>
                    </Box>
                )}

                <EstimateSupplierComparison
                    projectId={projectId}
                    materialsByGroup={materialsByGroup}
                    onSuppliersApplied={() =>
                        setPricingRefreshKey((key) => key + 1)
                    }
                />
            </Stack>
        </Box>
    );
}

function GroupRows({ rows, handlers }: GroupRowsProps) {
    const nodes: ReactNode[] = [];
    let furnaceRows: FurnaceVentMaterialRow[] = [];

    const flushFurnace = () => {
        if (furnaceRows.length === 0) return;
        nodes.push(
            <FurnaceVentCluster
                key={`furnace-${nodes.length}`}
                rows={furnaceRows}
                handlers={handlers}
            />,
        );
        furnaceRows = [];
    };

    for (const row of rows) {
        if (isFurnaceVentRow(row)) {
            furnaceRows.push(row);
            continue;
        }

        flushFurnace();
        nodes.push(<MaterialRow key={row.id} row={row} handlers={handlers} />);
    }

    flushFurnace();
    return <>{nodes}</>;
}

function FurnaceVentCluster({ rows, handlers }: FurnaceVentClusterProps) {
    const bySize = buildFurnaceVentTreeBySize(rows);

    return (
        <MaterialGroupBlock
            title="Furnace Vent"
            subtitle="Jack, cap, and collar per size"
        >
            {bySize.map((branch) => (
                <MaterialSubgroupBlock key={branch.size} title={branch.size}>
                    {branch.lines.map((line) => (
                        <EditableMaterialCard
                            key={line.lineId}
                            editKey={line.lineId}
                            rowId={line.lineId}
                            category={line.category}
                            item={{
                                productName: line.category,
                                lineLabel: line.category,
                                availableProducts: line.availableProducts,
                                selectedProductId: line.selectedProductId,
                                quantity: line.quantity,
                                priceRange: line.priceRange,
                                variant: line.type,
                                detail: line.flashingKit,
                                showCategoryInCard: false,
                                inGroup: true,
                                colors: line.colors,
                            }}
                            handlers={handlers}
                        />
                    ))}
                </MaterialSubgroupBlock>
            ))}
        </MaterialGroupBlock>
    );
}

function MaterialRow({ row, handlers }: MaterialRowProps) {
    const isFieldShingles = row.category === FIELD_SHINGLES_CATEGORY;

    if (row.mode === "single") {
        const edit = handlers.edits[row.id];
        const selectedId =
            edit?.selectedProductId ?? row.selectedProductId ?? null;
        const multiValues =
            handlers.fieldShingleMultiValues &&
            handlers.fieldShingleMultiValues.length > 0
                ? handlers.fieldShingleMultiValues
                : selectedId
                  ? [selectedId]
                  : [];

        return (
            <EditableMaterialCard
                editKey={row.id}
                rowId={row.id}
                category={row.category}
                item={{
                    ...row,
                    showCategoryInCard: true,
                }}
                handlers={handlers}
                productMultiSelect={isFieldShingles}
                productMultiValues={isFieldShingles ? multiValues : undefined}
                onProductMultiChange={
                    isFieldShingles
                        ? (optionKeys) =>
                              handlers.onFieldShingleMultiChange?.(
                                  row.id,
                                  row.id,
                                  optionKeys,
                                  row.availableProducts,
                              )
                        : undefined
                }
            />
        );
    }

    return (
        <MaterialGroupBlock title={row.category}>
            {row.lines.map((line) => (
                <EditableMaterialCard
                    key={line.lineId}
                    editKey={line.lineId}
                    rowId={line.lineId}
                    category={row.category}
                    item={{
                        productName: row.category,
                        lineLabel: line.size,
                        availableProducts: line.availableProducts,
                        selectedProductId: line.selectedProductId,
                        quantity: line.quantity,
                        priceRange: line.priceRange,
                        showCategoryInCard: false,
                        inGroup: true,
                        colors: line.colors,
                    }}
                    handlers={handlers}
                />
            ))}
        </MaterialGroupBlock>
    );
}

function EditableMaterialCard({
    editKey,
    rowId,
    category,
    item,
    handlers,
    productMultiSelect,
    productMultiValues,
    onProductMultiChange,
}: EditableMaterialCardProps & {
    productMultiSelect?: boolean;
    productMultiValues?: string[];
    onProductMultiChange?: (optionKeys: string[]) => void;
}) {
    const edit = handlers.edits[editKey];
    const selectedProductId = edit
        ? edit.selectedProductId
        : item.selectedProductId;
    const quantity = edit ? edit.quantity : item.quantity;
    const supplierUnitPrice =
        edit?.supplierUnitPrice ?? item.supplierUnitPrice ?? null;
    const supplierId = edit?.supplierId ?? item.supplierId ?? null;
    const supplierName = edit?.supplierName ?? item.supplierName ?? null;
    const formulaFlag = handlers.formulaFlags[editKey];
    const catalogColors = item.colors ?? [];
    const showCatalogColor = catalogColors.length > 0;
    const showFreeTextColor =
        category === FIELD_SHINGLES_CATEGORY && !showCatalogColor;
    const color = showCatalogColor
        ? (edit?.selectedColor ?? item.color ?? "")
        : showFreeTextColor
          ? (handlers.getColor?.(category) ?? "")
          : undefined;

    return (
        <MaterialCard
            item={{
                ...item,
                selectedProductId,
                quantity,
                quantityOverridden: edit?.quantityOverridden,
                supplierUnitPrice,
                supplierId,
                supplierName,
                hasFormula: formulaFlag?.hasFormula,
                formulaActive: formulaFlag?.formulaActive,
                formula: formulaFlag,
                showColorInput: showFreeTextColor,
                colors: catalogColors,
                color,
            }}
            productMultiSelect={productMultiSelect}
            productMultiValues={productMultiValues}
            onProductMultiChange={onProductMultiChange}
            onProductChange={(optionKey) =>
                handlers.onProductChange(
                    editKey,
                    category,
                    rowId,
                    optionKey,
                    item.availableProducts,
                )
            }
            onQuantityChange={(nextQuantity) => {
                handlers.onQuantityChange(editKey, nextQuantity);
            }}
            onResetToFormula={() => handlers.onResetToFormula(editKey)}
            onColorChange={
                showCatalogColor
                    ? (next) =>
                          handlers.onCatalogColorChange?.(
                              editKey,
                              next,
                              item.availableProducts,
                          )
                    : showFreeTextColor
                      ? (next) => handlers.onColorChange?.(category, next)
                      : undefined
            }
        />
    );
}

function isFurnaceVentRow(row: MaterialRowItem): row is FurnaceVentMaterialRow {
    return row.mode === "multi" && row.derivedFrom === "Furnace Vent";
}
